// Storefront tenant selection is explicit. Locale, timezone, and customer
// country only affect text/formatting, never the active company.
import {
  currencyForCompany,
  localeForCompany,
  readCompanyProfile,
} from "lib/companyProfile";
import { normalizeStorefrontLocale } from "lib/storefrontCopy";

const STOREFRONT_TENANT_STORAGE_KEY = "vbs.storefrontTenant";

const normalizeCompanyKey = (value) => {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(v) ? v : null;
};

export const TEXT_LOCALE_STORAGE_KEY = "vbsTextLocale";

const normalizeTextLocale = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "es" || raw.startsWith("es-")) return "es-MX";
  if (raw === "en" || raw.startsWith("en-")) return "en-US";
  return "";
};

export const browserLanguage = () => {
  if (typeof navigator === "undefined") return "en-US";
  const languages = Array.from(
    navigator.languages || [navigator.language]
  ).filter(Boolean);
  return normalizeTextLocale(languages[0]) || "en-US";
};

export function getTextLocale() {
  if (typeof window !== "undefined") {
    try {
      const stored = normalizeTextLocale(
        localStorage.getItem(TEXT_LOCALE_STORAGE_KEY)
      );
      if (stored) return stored;
    } catch {
      // ignore storage errors
    }
  }
  return browserLanguage();
}

export function setTextLocale(locale) {
  const next = normalizeTextLocale(locale) || "en-US";
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(TEXT_LOCALE_STORAGE_KEY, next);
    } catch {
      // ignore storage errors
    }
  }
  return next;
}

export function resolveStorefrontCompanyKey({
  tenantKey = null,
  activeCompany = null,
} = {}) {
  return (
    normalizeCompanyKey(tenantKey) ||
    normalizeCompanyKey(activeCompany) ||
    readStorefrontTenantKey()
  );
}

export function resolveStorefrontLocale({
  tenant = null,
  tenantKey = null,
  companyKey = null,
} = {}) {
  const key =
    normalizeCompanyKey(tenantKey) ||
    normalizeCompanyKey(companyKey) ||
    normalizeCompanyKey(tenant?.companyKey || tenant?.key);
  if (key === "mx") return "es-MX";
  return (
    normalizeStorefrontLocale(tenant?.defaultLocale) ||
    localeForCompany(key, "") ||
    getTextLocale() ||
    "en-US"
  );
}

export function getStorefrontConfig() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const tenant = readStorefrontTenant();
  const companyKey = resolveStorefrontCompanyKey({
    tenantKey: tenant?.companyKey || tenant?.key,
  });
  const profile = companyKey ? readCompanyProfile(companyKey) : null;
  const locale = resolveStorefrontLocale({ tenant, companyKey });
  const currency =
    profile?.baseCurrency ||
    tenant?.baseCurrency ||
    (companyKey ? currencyForCompany(companyKey) : "");

  return {
    hostname: host,
    forcedCompanyKey: null,
    defaultCompanyKey: null,
    companyKey,
    locale,
    currency,
    isMx: currency === "MXN" || companyKey === "mx",
  };
}

export function getFallbackCompanyKey() {
  return readStorefrontTenantKey();
}

export function readStorefrontTenant() {
  if (typeof window === "undefined") return null;
  try {
    const raw = JSON.parse(
      localStorage.getItem(STOREFRONT_TENANT_STORAGE_KEY) || "null"
    );
    const key = normalizeCompanyKey(raw?.companyKey || raw?.key);
    if (!key) return null;
    if (raw?.storefrontEnabled === false) {
      clearStorefrontTenant();
      return null;
    }
    return { ...raw, companyKey: key, key };
  } catch {
    return null;
  }
}

export function readStorefrontTenantKey() {
  return normalizeCompanyKey(readStorefrontTenant()?.companyKey);
}

export function writeStorefrontTenant(row) {
  if (typeof window === "undefined") return null;
  const key = normalizeCompanyKey(row?.companyKey || row?.key);
  if (!key) return null;
  if (row?.storefrontEnabled === false) {
    clearStorefrontTenant();
    return null;
  }
  const next = { ...(row || {}), companyKey: key, key };
  try {
    localStorage.setItem(STOREFRONT_TENANT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage errors
  }
  return next;
}

export function clearStorefrontTenant() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STOREFRONT_TENANT_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

export function ensureStorefrontCompanyContext() {
  return readStorefrontTenantKey();
}

export function bootstrapStorefront() {
  const cfg = getStorefrontConfig();
  if (cfg.locale) setTextLocale(cfg.locale);

  // Set page language (helps accessibility and browser translate behavior).
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = cfg.locale;
  }

  return cfg;
}
